import React from 'react';

import type { UUID } from '@aline/common';
import { Select } from '@aline/common';
import { Grid } from '@mui/material';
import { AlineDNDEditorContext, LeftNavDrawer } from 'src/components';
import { CheckoutDialog } from 'src/components/admin/billing/Checkout';
import { UserAlerts } from 'src/components/dashboard/UserAlerts';
import { useWalkthroughActive } from 'src/editor';
import { IS_MOBILE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { FocusedViewProvider, useIsActiveTabFocused } from './focused-view';
import { GlobalDrawerProvider, GlobalRightDrawer } from './GlobalRightDrawer';
import { GlobalTopBar } from './GlobalTopBar';
import { TabProvider } from './tabs';
import { TabActivityArea } from './tabs/tab-activity-area';
import { TAB_PANEL_ID } from './tabs/tab-bar';
import { useTabContext } from './tabs/tab-context';

const useStyles = makeStyles()(_theme => ({
  belowTopBar: {
    display: 'flex',
    flex: '1 1 0',
    minHeight: 0,
    overflow: 'hidden',
  },
  contentColumn: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  main: { display: 'flex', height: ['100vh', '100dvh'], overflow: 'hidden' },
  pageContent: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    // Required for the tab content container to fill the available app area.
    position: 'relative',
  },
  rightSide: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
  },
}));

export function AppLayout(): JSX.Element {
  return (
    <TabProvider>
      <AppLayoutContent />
    </TabProvider>
  );
}

function AppLayoutContent(): JSX.Element {
  const { activeTab } = useTabContext();

  return (
    <FocusedViewProvider
      activeTabId={activeTab?.id ?? null}
      activeTabRoute={activeTab?.route ?? null}
    >
      <AppLayoutShell activeTabId={activeTab?.id ?? null} />
    </FocusedViewProvider>
  );
}

function AppLayoutShell({ activeTabId }: { activeTabId: UUID | null }): JSX.Element {
  const { classes } = useStyles();
  const isFocused = useIsActiveTabFocused();
  const { walkThroughActive } = useWalkthroughActive();

  return (
    <GlobalDrawerProvider>
      <AlineDNDEditorContext activeTabId={activeTabId}>
        <Grid container className={classes.main} wrap="nowrap">
          {!walkThroughActive && !isFocused && <LeftNavDrawer />}
          <div className={classes.rightSide} data-cy={Select.appLayout.rightSide}>
            {!walkThroughActive && <GlobalTopBar />}
            <div className={classes.belowTopBar} id={TAB_PANEL_ID} role="tabpanel">
              <div className={classes.contentColumn}>
                <div className={classes.pageContent}>
                  {!isFocused && <UserAlerts />}
                  <CheckoutDialog />
                  <TabActivityArea />
                </div>
              </div>
              {(!walkThroughActive || !IS_MOBILE) && <GlobalRightDrawer />}
            </div>
          </div>
        </Grid>
      </AlineDNDEditorContext>
    </GlobalDrawerProvider>
  );
}
